import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { themeGet } from 'styled-system';
import MarkdownToJsx from 'markdown-to-jsx';
import { Heading, Link, Text } from 'roo-ui/components';
import startCase from 'lodash/startCase';
import { useDataLayer } from 'hooks/useDataLayer';
import { mediaQuery } from 'lib/styledSystem';

const BlockQuote = styled(Text)`
  display: block;
  font-size: ${themeGet('fontSizes.base')};

  ${mediaQuery.minWidth.sm} {
    font-size: ${themeGet('fontSizes.lg')};
  }

  > span {
    font-size: ${themeGet('fontSizes.base')};

    ${mediaQuery.minWidth.sm} {
      font-size: ${themeGet('fontSizes.lg')};
    }
  }
`;

const Paragraph = styled(Text)`
  display: block;
  font-size: ${themeGet('fontSizes.base')};
  padding-bottom: ${themeGet('space.4')};
`;

const Span = styled(Text)`
  display: inline-block;
  font-size: ${themeGet('fontSizes.base')};
  padding-bottom: ${themeGet('space.4')};
`;

const Heading1 = styled(Heading.h1)`
  font-size: ${themeGet('fontSizes.2xl')};

  ${mediaQuery.minWidth.sm} {
    font-size: ${themeGet('fontSizes.3xl')};
  }
`;

const Heading3 = styled(Heading.h3)`
  text-align: center;
  margin-bottom: ${themeGet('space.4')};
  margin-top: ${themeGet('space.10')};
`;

const Heading4 = styled(Heading.h4)`
  font-weight: ${themeGet('fontWeights.normal')};
`;

const InlineLink = styled(Link)`
  display: inline;
`;

const qantasHotelsPropertyRegExp = new RegExp(/^https:\/\/www\.qantas\.com\/hotels\/properties\/(?:[0-9]*)?$/);

const LinkWithTracking = ({ children, href, ...props }) => {
  const { emitInteractionEvent } = useDataLayer();

  const handleClick = useCallback(() => {
    const isQHProperty = qantasHotelsPropertyRegExp.test(href);
    if (isQHProperty && children) {
      emitInteractionEvent({ type: 'Hotel Text Link', value: `${startCase(children)} Selected` });
    }
  }, [children, href, emitInteractionEvent]);

  return (
    <InlineLink onClick={handleClick} href={href} {...props} rel="noopener" target="_blank">
      {children}
    </InlineLink>
  );
};

LinkWithTracking.propTypes = {
  href: PropTypes.string,
  children: PropTypes.node,
};

LinkWithTracking.defaultProps = {
  href: undefined,
  children: null,
};

const options = {
  overrides: {
    a: LinkWithTracking,
    blockquote: BlockQuote,
    h1: Heading1,
    h2: Heading.h2,
    h3: Heading3,
    h4: Heading4,
    h5: Heading.h5,
    p: Paragraph,
    span: Span,
  },
};

const Markdown = ({ content, ...rest }) => (
  <MarkdownToJsx options={options} {...rest}>
    {content}
  </MarkdownToJsx>
);

Markdown.propTypes = {
  content: PropTypes.string,
};

Markdown.defaultProps = {
  content: '',
};

export default Markdown;
